import { t } from '@ctcells/i18n'

import { Segmented } from '../../atoms/Segmented.tsx'
import { SettingsRow } from '../../layout/SettingsList.tsx'
import type { ActionGate } from '../access.ts'

import './generalSettings.css'

/**
 * 관리 › 설정의 줄 두 가지.
 *
 * ## 🔴 「변경」도 「저장」도 없다 (eric 2026-09-22)
 *
 * eric 원문: 「**저장 버튼 넣지 말고, 값 바꾸면 해당 값이 적용되게** 해줘」.
 * `ChoiceRow` 는 **고르는 즉시** 나간다. 확정하는 단추가 없다.
 *
 * ## 🔴 낙관적 갱신이 없다 (DP-04)
 *
 * 보이는 값은 **언제나 `value` prop**(= 서버가 준 값)이다. 고른 뒤 스스로 칠하지 않는다 —
 * 값이 바뀌는 것은 무효화·재조회로 새 값이 내려온 결과이고, 거절되면 값은 그대로 있고
 * 사유만 붙는다.
 *
 * ## 🔴 값을 지어내지 않는다 — `MissingSettingRow`
 *
 * 서버에 조회 경로가 없는 값은 **조작을 그리지 않고 「—」**다. 종전에는 와이어프레임 숫자를
 * 기본값으로 세워 두었는데 **그 숫자가 실물과 달랐다**(히나 실측 2026-09-22: 무활동 만료가
 * 화면 30분 ↔ 서버 `.env` 12시간, 최소 길이 12자 ↔ 서버 8, 복잡도 「상」 ↔ 서버 꺼짐).
 * 비활성이어도 숫자가 서 있으면 **「지금 값이 그거구나」로 읽힌다** — 그게 빈 칸보다 나쁘다.
 */

/** 한 칸의 서버 연결 상태. **`onApply` 가 없으면 그 칸은 서버가 아직 받지 않는다** */
export interface FieldIo<V> {
  onApply?: ((value: V) => void) | undefined
  /** 보내는 중 — 그 줄만 비활성 */
  pending?: boolean | undefined
  /** 서버가 거절한 사유. 뜨면 값은 서버 값 그대로 남고 사유만 붙는다 */
  error?: string | undefined
}

/**
 * 줄 게이트 — 사유는 **하나만** 고른다.
 *
 * ```
 * 권한/락/상태 → 보내는 중 → 서버 경로 없음
 * ```
 */
function fieldGate(base: ActionGate, io: FieldIo<never> | undefined): ActionGate {
  if (base.disabled) return base
  if (io?.pending === true) return { disabled: true, disabledReason: t('reason.requestInFlight') }
  if (io?.onApply === undefined)
    return { disabled: true, disabledReason: t('reason.noServerRoute') }
  return { disabled: false }
}

export interface ChoiceRowProps<V extends string> {
  label: string
  value: V
  options: readonly { value: V; label: string }[]
  io?: FieldIo<V> | undefined
  /** 권한·락·상태 게이트. 여기에 `io`(보내는 중 · 경로 없음)가 얹힌다 */
  baseGate: ActionGate
}

/**
 * 선택지 줄 — 고르는 즉시 나간다.
 *
 * 🔴 못 고르면 `onChange` 를 안 넘긴다 → 구획이 `aria-disabled` 로 선다. `Segmented` 는
 *    사유 슬롯이 없는 원자라(고치지 않는다) **보조기술이 읽는 숨은 글자**를 여기서 단다.
 *    `title` 만으로는 키보드·스크린리더 사용자가 왜 못 고르는지 알 수 없다.
 */
export function ChoiceRow<V extends string>({
  label,
  value,
  options,
  io,
  baseGate,
}: ChoiceRowProps<V>) {
  const rowGate = fieldGate(baseGate, io)
  const apply = io?.onApply
  return (
    <SettingsRow
      action={
        <span
          className="gset__ctl"
          {...(rowGate.disabled ? { title: rowGate.disabledReason } : {})}
        >
          <Segmented
            label={label}
            options={options}
            value={value}
            {...(rowGate.disabled || apply === undefined ? {} : { onChange: apply })}
          />
          {rowGate.disabled ? <span className="a-sr">{rowGate.disabledReason}</span> : null}
          {io?.error === undefined ? null : (
            /* 🔴 색만으로 말하지 않는다 — `role="alert"` 로 읽힌다 */
            <span className="gset__err" role="alert">
              {io.error}
            </span>
          )}
        </span>
      }
      label={label}
      value={null}
    />
  )
}

/**
 * 서버가 값을 못 준 줄 — **라벨 + 「—」가 전부**다. 조작도 사유 문구도 없다.
 *
 * ⚠ 지금 쓰는 곳은 **운영 모드 줄의 슬롯이 안 넘어온 경우**(목 경로) 하나뿐이다.
 *   서버에 경로 자체가 없는 설정 17줄은 2026-09-28 에 자리째 걷었다(eric 「정할 수 없으면 내용 다
 *   빼버려」) — 🔴 이 줄로 그 자리들을 되살리지 마라. `GeneralSettings.tsx` 머리말.
 * 🔴 **비활성 조작으로 두지 않는다.** 흐린 「30 분」은 placeholder 가 아니라 «지금 값»으로
 *    읽힌다(`TextInput` 머리말의 그 사고와 같은 성질이다).
 */
export function MissingSettingRow({ label }: { label: string }) {
  return <SettingsRow label={label} value={<span className="muted">{t('mask.none')}</span>} />
}
